import React, { ReactElement, useCallback, useMemo, useState } from 'react'
import { PrimaryButton } from './Button'
import Radom from '../state/Radom'
import { InputLabel } from './Input'
import { EXPORT_COLUMN_TYPES, EXPORT_PAYOUT_ENTRIES_COLUMNS as COLUMNS } from '../util/Constants'
import Checkbox from './Checkbox'
import { downloadBlob, errorToast, getExportFileName } from '../util/Util'
import { cloneDeep } from 'lodash'
import Dropdown, { DropdownItem } from './Dropdown'
import {
  containerStyle,
  sectionStyle,
  dropdownWrapperStyle,
  dropdownOuterStyle,
  dropdownContentStyle,
  gridStyle,
  columnRowStyle,
  footerStyle,
  exportButtonStyle
} from './ExportPayoutEntriesFormStyles'

const ExportPayoutEntriesForm = ({ id }: { id: string }): ReactElement => {
  const [isLoading, setIsLoading] = useState(false)
  const [closeFn, setCloseFn] = useState(() => () => {})
  const [columnTypes, setColumnTypes] = useState(EXPORT_COLUMN_TYPES.ALL_COLUMNS)
  const [columns, setColumns] = useState(cloneDeep(COLUMNS))

  const columnsAsStrings = useMemo(() =>
    Object.entries(columns).filter(([_, v]) => !v.unchecked).map(([k]) => k), [columns])
  const shouldShowAllColumns = columnTypes === EXPORT_COLUMN_TYPES.ALL_COLUMNS
  const shouldShowCustomColumns = columnTypes === EXPORT_COLUMN_TYPES.CUSTOM

  const handleColumnToggle = useCallback(
    (columnKey: string) => {
      setColumns(prevColumns => {
        const nextColumns = cloneDeep(prevColumns)
        nextColumns[columnKey].unchecked = !nextColumns[columnKey].unchecked
        return nextColumns
      })
    },
    []
  )

  const handleColumnTypeSelect = useCallback(
    (columnType: EXPORT_COLUMN_TYPES): void => {
      if (columnType === EXPORT_COLUMN_TYPES.ALL_COLUMNS) {
        setColumns(cloneDeep(COLUMNS))
      }

      setColumnTypes(columnType)
      closeFn()
    },
    [setColumns, setColumnTypes, closeFn]
  )

  const exportPayoutEntries = useCallback(async (): Promise<void> => {
    setIsLoading(true)

    try {
      const data = await Radom.exportPayoutEntries(
        id,
        columnsAsStrings
      )

      const blob = new Blob([data], { type: 'text/csv' })
      const fileName = getExportFileName('payout_entries')

      downloadBlob(blob, fileName)
    } catch (err: any) {
      errorToast(err.response?.data?.error || err.message)
    } finally {
      setIsLoading(false)
    }
  }, [
    id,
    columnsAsStrings,
    getExportFileName,
    downloadBlob,
    errorToast
  ])

  return <>
    <div style={containerStyle}>
      <div style={sectionStyle}>
        <InputLabel>Columns</InputLabel>
        <div style={dropdownWrapperStyle}>
          <Dropdown
            disabled={isLoading}
            outerStyle={dropdownOuterStyle}
            onCloseFn={_closeFn => setCloseFn(() => _closeFn)}
            selectedContent={<span>{columnTypes}</span>}
            dropdownContent={
              <div style={dropdownContentStyle}>
                {Object.values(EXPORT_COLUMN_TYPES)
                  .filter(c => c !== columnTypes)
                  .map((c, i) => (
                    <DropdownItem
                      key={i}
                      onClick={handleColumnTypeSelect.bind(null, c)}
                    >
                      {c}
                    </DropdownItem>
                  ))}
              </div>
            }
          />
        </div>

        {shouldShowCustomColumns && (
          <div
            style={gridStyle}
          >
            {Object.entries(columns).map(([columnKey, columnValue]) => (
              <div
                key={columnKey}
                style={columnRowStyle}
              >
                <Checkbox
                  size={18}
                  checked={!columnValue.unchecked}
                  disabled={isLoading}
                  onClick={handleColumnToggle.bind(null, columnKey)}
                />
                <span>{columnValue.label}</span>
              </div>
            ))}
          </div>
        )}

        {shouldShowAllColumns && (
          <span>
            {Object.entries(columns)
              .map(([_, l]) => l.label)
              .join(', ')}
          </span>
        )}
      </div>
    </div>

    <div style={footerStyle}>
      <PrimaryButton
        style={exportButtonStyle}
        disabled={columnsAsStrings.length < 1}
        onClick={exportPayoutEntries}
        isLoading={isLoading}>
      Export Payout Entries
      </PrimaryButton>
    </div>
  </>
}

export default ExportPayoutEntriesForm
